var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Timeouts } from '../framework/Timeouts';
import { TransportError } from '../framework/Transport';
export class ViscaBase {
    constructor(_driverType, state, log, framework) {
        this._driverType = _driverType;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // static properties
        // other class properties and constructor
        this.cameraInfo = {};
        this.commandResponseStyle = 'standard';
        // This is the power on timeout for cameras without deep sleep. Some cameras can take a while to
        // start responding after powering back on.
        this.powerOnTimeoutMs = 55000;
        this.timeouts = new Timeouts();
        this.usedIds = {};
        // Different cameras have different commands and behaviors for some features (e.g., camera
        // tracking and preset ranges). We describe them here in two ways. First, we can describe them
        // with a device-specific behavior, which is detected by the equipment type ID. Second, we can
        // auto-detect them by querying the feature and confirming the response.
        this.detectedBehaviors = [
            {
                // Aver CAM550 (dynamically detected)
                tracking: {
                    get: (cameraId, tags) => __awaiter(this, void 0, void 0, function* () {
                        const resp = yield this.send([0x80 + cameraId, 0x09, 0x04, 0x7d], (resp) => this.cmdIsCompletion(resp, cameraId, 6) ? resp : undefined, tags);
                        return resp.charCodeAt(this.commandResponseStyle === 'standard' ? 4 : 3) !== 1;
                    }),
                    set: (cameraId, value, tags) => __awaiter(this, void 0, void 0, function* () {
                        const track = value ? 0x02 : 0x01;
                        yield this.send([0x80 + cameraId, 0x01, 0x04, 0x7d, track, 0x00], (resp) => this.cmdIsCompletion(resp, cameraId, 2) ? resp : undefined, tags, 
                        // Aver camera over UDP doesn't respond so have short timeout.
                        { sendTimeoutMs: 500 });
                        return value;
                    }),
                },
            },
            {
                // Aver TR3xx (dynamically detected)
                tracking: {
                    get: (cameraId, tags) => __awaiter(this, void 0, void 0, function* () {
                        const resp = yield this.send([0x80 + cameraId, 0x09, 0x36, 0x69, 0x02], (resp) => this.cmdIsCompletion(resp, cameraId, 3) ? resp : undefined, tags);
                        return resp.charCodeAt(2) === 1;
                    }),
                    set: (cameraId, value, tags) => __awaiter(this, void 0, void 0, function* () {
                        const track = value ? 0x02 : 0x03;
                        yield this.send([0x80 + cameraId, 0x01, 0x04, 0x7d, track], (resp) => this.cmdIsCompletion(resp, cameraId, 2) ? resp : undefined, tags);
                        return value;
                    }),
                },
            },
            {
                // For TCP motion control, we append a power query after the move command. The ClearOne
                // Unite 200 (and possibly other cameras) does not return any response to move commands.
                // To work around this, we append a power query after the move command. Since some
                // cameras don't respond to the power query and other cameras only respond to the move
                // command, we look for either.
                driverType: ['socketDriver'],
                motionControl: {
                    set: (cameraId, cmd, tags) => __awaiter(this, void 0, void 0, function* () {
                        cmd.push(...[0xff, 0x80 + cameraId, 0x09, 0x04, 0x00]);
                        yield this.send(cmd, (resp) => this.cmdIsCompletion(resp, cameraId, 2) ||
                            this.cmdIsCompletion(resp, cameraId, 3)
                            ? resp
                            : undefined, tags);
                    }),
                },
            },
            {
                // Standard motion behavior for UDP -- just send the command as-is.
                driverType: ['udpDriver'],
                motionControl: {
                    set: (cameraId, cmd, tags) => __awaiter(this, void 0, void 0, function* () {
                        yield this.send(cmd, (resp) => this.cmdIsCompletion(resp, cameraId, 2) ||
                            this.cmdIsCompletion(resp, cameraId, 3)
                            ? resp
                            : undefined, tags);
                    }),
                },
            },
        ];
        this.deviceSpecificBehaviors = [
            {
                // Alfatron ALF-12X/20X/30X-SDIC tracking.
                equipmentTypeIds: ['-OLpXLEG-mVM2cfx_r94'],
                tracking: {
                    get: (cameraId, tags) => __awaiter(this, void 0, void 0, function* () {
                        var _a;
                        // There is no tracking query command.
                        return (_a = this.state.cameraTracking.values[cameraId.toString()]) !== null && _a !== void 0 ? _a : false;
                    }),
                    set: (cameraId, value, tags) => __awaiter(this, void 0, void 0, function* () {
                        const track = value ? 0x02 : 0x03;
                        yield this.send([0x80 + cameraId, 0x0a, 0x01, 0x32, 0x00, 0x00, track, 0x00], (resp) => this.cmdIsCompletion(resp, cameraId, 2) ? resp : undefined, tags);
                        return value;
                    }),
                },
            },
            {
                // Aver CAM550
                equipmentTypeIds: ['-NMecRbuvPj8kjVPZMns', '-NwUg4eVRrcPA2QTNaJ5'],
                presetRange: { max: 127, min: 0 },
                // Aver CAM550 has camera tracking, but we describe that as a detected behavior.
            },
            {
                // Crestron IV-CAMA3
                equipmentTypeIds: ['-NTzGq8lgK9sZEJzSgwd'],
                driverType: ['socketDriver'],
                onConfigure: (config, cameraConfig, first) => __awaiter(this, void 0, void 0, function* () {
                    // Enable the heartbeat (preset 200 = 0xc8) for detecting camera tracking state.
                    try {
                        yield this.send([0x80 + cameraConfig.id, 0x01, 0x04, 0x3f, 0x02, 0xc8], (resp) => this.cmdIsCompletion(resp, cameraConfig.id, 2) ? resp : undefined, ['set.heartbeat', 'configuring']);
                    }
                    catch (error) {
                        if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                            throw error;
                        }
                    }
                }),
                presetRange: { max: 79, min: 10 },
                tracking: {
                    get: (cameraId, tags) => __awaiter(this, void 0, void 0, function* () {
                        var _b;
                        return (_b = this.state.cameraTracking.values[cameraId.toString()]) !== null && _b !== void 0 ? _b : false;
                    }),
                    set: (cameraId, value, tags) => __awaiter(this, void 0, void 0, function* () {
                        // Recall camera tracking 'preset' to enable/disable tracking.
                        const track = value ? 0x50 : 0x51;
                        yield this.send([0x80 + cameraId, 0x01, 0x04, 0x3f, 0x02, track], (resp) => this.cmdIsCompletion(resp, cameraId, 2) ? resp : undefined, tags);
                        return value;
                    }),
                    asyncHandlers: [
                        {
                            // Async handler for heartbeat, which contains camera tracking status.
                            regExp: /\x30\x30\x30\x30\x01([\s\S])[\s\S]/i,
                            callback: (match) => {
                                var _a;
                                // Assume there's only one camera.
                                const key = (_a = Object.keys(this.cameraInfo)[0]) !== null && _a !== void 0 ? _a : 'unknown';
                                this.state.cameraTracking.update(key, match[1] === '\x01');
                            },
                        },
                    ],
                },
            },
            {
                // PTZ Optics G3 Series
                equipmentTypeIds: ['-NnPIXq3BhuW2xN3du7J'],
                driverType: ['socketDriver'],
                avoidInitialPowerOn: true,
                // Set the idle timeout large enough to not cause an idle timeout upon power on before the socket is
                // dropped by the camera during power on.
                idleTimeoutMs: 45000,
                power: {
                    set: (cameraId, power, tags) => __awaiter(this, void 0, void 0, function* () {
                        const currentPower = yield this.send([0x80 + cameraId, 0x09, 0x04, 0x00], (resp) => {
                            if (this.cmdIsCompletion(resp, cameraId, 3)) {
                                return resp.charCodeAt(2) === 2 ? 'on' : 'off';
                            }
                            else if (this.cmdIsNotExecutable(resp, cameraId)) {
                                // If we're getting error responses from the camera
                                // then we assume the camera is powered off
                                return 'off';
                            }
                            return undefined;
                        }, ['power.query']);
                        // Don't send a power command unless you have to since this model will restart
                        // the camera upon power on
                        if ((currentPower === 'on' && power) || (currentPower === 'off' && !power)) {
                            return power;
                        }
                        try {
                            yield this.send([0x80 + cameraId, 0x01, 0x04, 0x00, power ? 0x02 : 0x03], (resp) => {
                                if (this.cmdIsCompletion(resp, cameraId))
                                    return resp;
                                if (this.cmdIsError(resp, cameraId))
                                    return resp;
                                return undefined;
                            }, tags, 
                            // Use a short timeout since some cameras won't respond and we're going
                            // to send a query anyway.
                            power ? { sendTimeoutMs: 500 } : {});
                        }
                        catch (error) {
                            if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                                throw error;
                            }
                        }
                        return power;
                    }),
                },
                tracking: {
                    // There is no query command, so return state
                    get: (cameraId, tags) => __awaiter(this, void 0, void 0, function* () {
                        var _c;
                        return (_c = this.state.cameraTracking.values[cameraId.toString()]) !== null && _c !== void 0 ? _c : false;
                    }),
                    set: (cameraId, value, tags) => __awaiter(this, void 0, void 0, function* () {
                        const track = value ? 0x02 : 0x03;
                        yield this.send([0x80 + cameraId, 0x0a, 0x11, 0x54, track], (resp) => this.cmdIsCompletion(resp, cameraId, 2) ? resp : undefined, tags);
                        return value;
                    }),
                },
            },
        ];
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.cameras = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _d, _e;
                return {
                    name: (_d = this.cameraInfo[key]) === null || _d === void 0 ? void 0 : _d.name,
                    hasCameraTracking: ((_e = this.cameraInfo[key]) === null || _e === void 0 ? void 0 : _e.tracking) !== undefined,
                    hasPan: true,
                    hasTilt: true,
                    hasZoom: true,
                    location: 'near',
                };
            }),
            getKey: (config) => {
                if (!('id' in config)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(config)}`);
                    return 'invalid';
                }
                return config.id.toString();
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!('id' in config))
                    return 'incompatible';
                const driverKey = config.id.toString();
                return this.cameraInfo[driverKey] === undefined ? 'missing' : 'ok';
            }),
        };
        this.cameraMove = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _f;
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraMove: unknown key ${JSON.stringify(key)}`);
                    return value;
                }
                this.state.cameraPreset.update(undefined);
                if ('direction' in value)
                    cameraInfo.moveDirection = value.direction;
                if (value.action === 'start') {
                    cameraInfo.moveSpeed = 'slow';
                    yield this.sendCameraMove('start', cameraInfo, tags);
                }
                else if (value.action === 'stop') {
                    yield this.sendCameraMove('stop', cameraInfo, tags);
                }
                else if (value.action === 'repeat') {
                    if (((_f = cameraInfo.moveSpeed) !== null && _f !== void 0 ? _f : 'slow') === 'slow') {
                        // TODO: can we just start again instead of having to stop first
                        yield this.sendCameraMove('stop', cameraInfo, tags);
                        cameraInfo.moveSpeed = 'fast';
                        yield this.sendCameraMove('start', cameraInfo, tags);
                    }
                    else {
                        yield this.sendCameraMove('continue', cameraInfo, tags);
                    }
                }
                return value;
            }),
        };
        this.cameraPosition = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraPosition: unknown key ${JSON.stringify(key)}`);
                    return { pan: 0, tilt: 0, zoom: 0 };
                }
                if (cameraInfo.presetRange === undefined) {
                    // Most cameras can save/restore position information, so we use that for presets.
                    const panTiltResp = yield this.send([0x80 + cameraInfo.id, 0x09, 0x06, 0x12], (resp) => this.cmdIsCompletion(resp, cameraInfo.id, 10) ? resp : undefined, [...tags, 'panTilt'], { retryAttempts: 1 });
                    const zoomResp = yield this.send([0x80 + cameraInfo.id, 0x09, 0x04, 0x47], (resp) => this.cmdIsCompletion(resp, cameraInfo.id, 6) ? resp : undefined, [...tags, 'zoom'], { retryAttempts: 1 });
                    return {
                        pan: this.convertPositionToNumber(panTiltResp.substring(2, 6)),
                        tilt: this.convertPositionToNumber(panTiltResp.substring(6, 10)),
                        zoom: this.convertPositionToNumber(zoomResp.substring(2, 6)),
                    };
                }
                else {
                    // It is better for some cameras to use built-in preset commands. For example, the
                    // Aver CAM550 has a bug where it doesn't restore the zoom value. So, we use the
                    // built-in presets, dynamically allocate a preset ID, and store the preset ID in
                    // the pan/tilt/zoom properties.
                    const usedIds = this.usedIds[key];
                    let found = false;
                    let presetNum = cameraInfo.presetRange.min;
                    while (usedIds !== undefined && presetNum <= cameraInfo.presetRange.max) {
                        if (!usedIds.includes(presetNum)) {
                            usedIds.push(presetNum);
                            found = true;
                            break;
                        }
                        presetNum++;
                    }
                    if (found) {
                        yield this.send([0x80 + cameraInfo.id, 0x01, 0x04, 0x3f, 0x01, presetNum], (resp) => this.cmdIsCompletion(resp, cameraInfo.id) ? resp : undefined, tags);
                    }
                    else {
                        this.log.error(`couldn't allocate preset number`);
                        presetNum = cameraInfo.presetRange.max;
                    }
                    return { pan: presetNum, tilt: presetNum, zoom: presetNum };
                }
            }),
        };
        this.cameraPreset = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.cameraPreset.value;
            }),
            set: (presetId, tags) => __awaiter(this, void 0, void 0, function* () {
                var _g, _h, _j, _k, _l, _m, _o;
                if (presetId === undefined)
                    return undefined;
                const preset = this.getPreset(presetId);
                if (preset === undefined) {
                    this.log.error(`preset "${presetId}" not found`);
                    return undefined;
                }
                const cameraId = preset.cameraId;
                const cameraInfo = this.cameraInfo[cameraId.toString()];
                if (cameraInfo === undefined) {
                    this.log.error(`camera not found for preset "${presetId}"`);
                    return undefined;
                }
                if (cameraInfo.presetRange === undefined) {
                    // Most cameras set pan/tilt directly. See comments in cameraPosition.
                    const pan = (_g = preset.pan) !== null && _g !== void 0 ? _g : 255;
                    const tilt = (_h = preset.tilt) !== null && _h !== void 0 ? _h : 255;
                    const panSpeed = cameraInfo.moveSpeed === 'slow'
                        ? (_j = cameraInfo.panSpeedSlow) !== null && _j !== void 0 ? _j : 3
                        : (_k = cameraInfo.panSpeedFast) !== null && _k !== void 0 ? _k : 8;
                    const tiltSpeed = cameraInfo.moveSpeed === 'slow'
                        ? (_l = cameraInfo.tiltSpeedSlow) !== null && _l !== void 0 ? _l : 1
                        : (_m = cameraInfo.tiltSpeedFast) !== null && _m !== void 0 ? _m : 8;
                    if (pan !== preset.pan || tilt !== preset.tilt) {
                        this.log.warning(`no position stored for preset "${presetId}", using defaults`);
                    }
                    const panTiltCmdBytes = [0x80 + cameraId, 0x01, 0x06, 0x02, panSpeed, tiltSpeed];
                    const panBytes = this.convertPositionToBytes(pan, 4);
                    const tiltBytes = this.convertPositionToBytes(tilt, 4);
                    yield this.send([...panTiltCmdBytes, ...panBytes, ...tiltBytes], (resp) => this.cmdIsCompletion(resp, cameraId) ? resp : undefined, tags, { sendTimeoutMs: 6000 } // the move may take a while
                    );
                    // set zoom
                    if (preset.zoom !== undefined) {
                        const zoomBytes = this.convertPositionToBytes(preset.zoom, 4);
                        yield this.send([0x80 + cameraId, 0x01, 0x04, 0x47, ...zoomBytes], (resp) => this.cmdIsCompletion(resp, cameraId) ? resp : undefined, tags, { sendTimeoutMs: 6000 } // the move may take a while
                        );
                    }
                }
                else {
                    // Some cameras use preset commands. See comments in cameraPosition.
                    const presetNum = (_o = preset.pan) !== null && _o !== void 0 ? _o : cameraInfo.presetRange.min;
                    yield this.send([0x80 + cameraInfo.id, 0x01, 0x04, 0x3f, 0x02, presetNum], (resp) => this.cmdIsCompletion(resp, cameraId) ? resp : undefined, tags);
                }
                return presetId;
            }),
        };
        this.cameraTracking = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraTracking: unknown key ${JSON.stringify(key)}`);
                    return this.state.cameraTracking.values[key];
                }
                if (!cameraInfo.tracking)
                    return false;
                return yield cameraInfo.tracking.get(cameraInfo.id, tags);
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraTracking: unknown key ${JSON.stringify(key)}`);
                    return value;
                }
                if (!cameraInfo.tracking)
                    return false;
                try {
                    value = yield cameraInfo.tracking.set(cameraInfo.id, value, tags);
                }
                catch (error) {
                    // Some cameras don't respond to the camera tracking set command (e.g., Aver).
                    if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                        throw error;
                    }
                }
                return value;
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _p;
                return (_p = this.cameraVersion) !== null && _p !== void 0 ? _p : '';
            }),
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                // Try each camera in order until one returns a version.
                this.cameraVersion = undefined;
                for (const key of Object.keys(this.cameraInfo).sort()) {
                    const camera = this.cameraInfo[key];
                    try {
                        let version = '';
                        this.cameraVersion = yield this.send([0x80 + camera.id, 0x09, 0x00, 0x02], (resp) => {
                            if (this.cmdIsCompletion(resp, camera.id, 9)) {
                                version = resp.substring(this.commandResponseStyle === 'standard' ? 2 : 1);
                            }
                            else {
                                return undefined;
                            }
                            let prettyVersion = '';
                            for (let i = 0; i < version.length; i++) {
                                if (i !== 0)
                                    prettyVersion += ' ';
                                if (version.charCodeAt(i) < 16)
                                    prettyVersion += '0';
                                prettyVersion += (version.charCodeAt(i) & 0x00ff).toString(16);
                            }
                            return prettyVersion;
                        }, ['hasControl.firmwareVersion']);
                    }
                    catch (_q) {
                        this.log.info(`firmwareVersion command timed out for camera id ${camera.id}`);
                    }
                    if (this.cameraVersion !== undefined)
                        break;
                }
                return this.cameraVersion !== undefined;
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return (yield this.queryPower(tags)) === 'on';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                this.state.busy.update(true);
                this.state.power.update(power);
                // Set all daisy-chained cameras to the same value. Some cameras don't support the
                // broadcast address, so send power commands to each camera individually.
                for (const camera of Object.values(this.cameraInfo)) {
                    if (!camera.hasPower)
                        continue;
                    if (camera.power) {
                        // Use device-specific power behavior for this camera.
                        yield camera.power.set(camera.id, power, tags);
                    }
                    else {
                        // Use standard power behavior.
                        try {
                            yield this.send([0x80 + camera.id, 0x01, 0x04, 0x00, power ? 0x02 : 0x03], (resp) => {
                                if (this.cmdIsCompletion(resp, camera.id))
                                    return resp;
                                if (this.cmdIsError(resp, camera.id))
                                    return resp;
                                return undefined;
                            }, tags, 
                            // Use a short timeout since some cameras won't respond and we're going
                            // to send a query anyway.
                            power ? { sendTimeoutMs: 500 } : {});
                        }
                        catch (error) {
                            if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                                throw error;
                            }
                        }
                    }
                }
                // Some cameras, such as the AT-HDVS-CAM, can go into a deep sleep mode where they don't
                // respond to any commands except power on. While powering back on, they still don't
                // respond to commands (other than sending the power on acks) for a while. We wait for
                // the cameras to power on by querying the power and waiting until they give a response.
                // If they don't respond, then we assume they are disconnected.
                //
                // This also accounts for cameras, such as the Angekis, which responds while powered off
                // but becomes unresponsive for 50+ seconds while powering on.
                let startTime = Date.now();
                if (power) {
                    for (const [key, camera] of Object.entries(this.cameraInfo)) {
                        if (!camera.hasPower)
                            continue;
                        let resp;
                        this.framework.suspendPolling(this.powerOnTimeoutMs + 5000);
                        startTime = Date.now();
                        while (Date.now() - startTime <= this.powerOnTimeoutMs) {
                            try {
                                resp = yield this.queryPower(undefined, key);
                            }
                            catch (error) {
                                if (!(error instanceof TransportError) ||
                                    error.code !== 'sendTimeout') {
                                    throw error;
                                }
                            }
                            if (resp !== undefined)
                                break;
                        }
                        this.framework.resume();
                        if (resp === undefined) {
                            this.framework.changePhase('disconnected');
                            return power;
                        }
                    }
                }
                this.state.busy.update(false);
                return power;
            }),
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                for (const camera of Object.values(this.cameraInfo)) {
                    if (camera.hasPower)
                        return true;
                }
                return false;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 3000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    var _r;
                    if (this.powerCameraKey !== undefined) {
                        // Query the first camera with power (if there is one).
                        const tags = ['get.power', 'poll'];
                        const power = (yield this.queryPower(tags, this.powerCameraKey)) === 'on';
                        this.state.power.update(power);
                    }
                    else {
                        // If there are no cameras with power, then query zoom on the first camera.
                        const tags = ['get.zoom', 'poll'];
                        const key = (_r = Object.keys(this.cameraInfo).sort()[0]) !== null && _r !== void 0 ? _r : '';
                        this.queryZoom(tags, key);
                    }
                }),
            },
        ];
    }
    // phase callbacks
    onBeforeConfigure(config) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            // It is possible we got here because we received a network change event and transitioned
            // to configuring as a result. We need to do a little housekeeping to clean things up.
            // Wait for any unhandled responses to clear out of the receive queue so they don't confuse
            // our detection later.
            yield this.timeouts.delay(1500);
            // It's possible a camera move command was being handled right as we transitioned from
            // controlling to configuring. We look for any cameras with active move timeouts and send
            // stop commands for them.
            for (const [driverKey, cameraInfo] of Object.entries(this.cameraInfo)) {
                if (cameraInfo.moveTimeout !== undefined) {
                    yield this.sendCameraMove('stop', cameraInfo, [
                        `set.cameraMove.${driverKey}`,
                        `configuring`,
                    ]);
                    cameraInfo.moveTimeout = undefined; // cleared above with timeouts.clearAll()
                }
            }
            const oldCameraInfo = this.cameraInfo;
            this.cameraInfo = {};
            this.powerCameraKey = undefined;
            this.usedIds = {};
            let allCamerasHaveDeepSleep = true;
            for (const cameraConfig of Object.values((_a = config.cameras) !== null && _a !== void 0 ? _a : {})) {
                if (!('id' in cameraConfig)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(cameraConfig)}`);
                    continue;
                }
                const driverKey = cameraConfig.id.toString();
                if (!cameraConfig.hasDeepSleep)
                    allCamerasHaveDeepSleep = false;
                // Detect cameras by sending a power query. If we get a valid response, then the camera
                // exists and supports power. If we get an error response, then the camera exists but
                // does not support power. If we get no response, then the camera might not exist.
                let powerResp;
                try {
                    powerResp = yield this.send([0x80 + cameraConfig.id, 0x09, 0x04, 0x00], (resp) => {
                        if (this.cmdIsCompletion(resp, cameraConfig.id, 3))
                            return 'ok';
                        if (this.cmdIsError(resp, cameraConfig.id))
                            return 'noPower';
                        return undefined;
                    }, [`validate.cameras.${driverKey}`, `get.power.${driverKey}`]);
                }
                catch (error) {
                    if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                        throw error;
                    }
                }
                if (powerResp === undefined) {
                    // Some cameras, such as the Birddog P120 and P200, don't support power and don't
                    // respond at all to power queries, so we look for a response from a zoom query
                    // before deciding the camera doesn't exist.
                    try {
                        yield this.send([0x80 + cameraConfig.id, 0x09, 0x04, 0x47], (resp) => {
                            if (this.cmdIsCompletion(resp, cameraConfig.id, 6))
                                return 'ok';
                            if (this.cmdIsError(resp, cameraConfig.id))
                                return 'noZoom';
                            return undefined;
                        }, [`validate.cameras.${driverKey}`, `get.power.${driverKey}`]);
                    }
                    catch (error) {
                        if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                            throw error;
                        }
                        continue; // no response, so assume camera isn't there
                    }
                }
                if (this.powerCameraKey === undefined && powerResp === 'ok') {
                    this.powerCameraKey = driverKey;
                }
                const cameraInfo = Object.assign(Object.assign({}, cameraConfig), { hasPower: powerResp === 'ok', 
                    // preserve move information in case start/stop happens across phase change
                    moveDirection: (_b = oldCameraInfo[driverKey]) === null || _b === void 0 ? void 0 : _b.moveDirection, moveSpeed: (_c = oldCameraInfo[driverKey]) === null || _c === void 0 ? void 0 : _c.moveSpeed });
                // First, see if there are device-specific behaviors.
                for (const behavior of this.deviceSpecificBehaviors) {
                    if (behavior.driverType !== undefined &&
                        !behavior.driverType.includes(this._driverType)) {
                        continue;
                    }
                    if (behavior.equipmentTypeIds.includes(config.equipmentType)) {
                        if (behavior.motionControl !== undefined) {
                            cameraInfo.motionControl = behavior.motionControl;
                        }
                        if (behavior.power !== undefined) {
                            cameraInfo.power = behavior.power;
                        }
                        if (behavior.presetRange !== undefined) {
                            cameraInfo.presetRange = behavior.presetRange;
                        }
                        if (behavior.tracking !== undefined) {
                            cameraInfo.tracking = behavior.tracking;
                        }
                        if (behavior.idleTimeoutMs !== undefined) {
                            this.powerOnTimeoutMs = behavior.idleTimeoutMs;
                        }
                    }
                }
                // Work around for legacy Aver Cam550 models that used the universal VISCA driver before
                // we supported the Aver Cam550 over UDP. If we detect we have an AVerCAM550 when using
                // a standard VISCA camera model, hardcode the presetRange information.
                if (this.commandResponseStyle === 'averCam550') {
                    cameraInfo.presetRange = { max: 127, min: 0 };
                }
                // If there is no equipment-specific tracking, then see if we can auto-detect camera
                // tracking.
                if (cameraInfo.tracking === undefined) {
                    for (const behavior of this.detectedBehaviors) {
                        if (behavior.driverType !== undefined &&
                            !behavior.driverType.includes(this._driverType)) {
                            continue;
                        }
                        if ('equipmentTypeId' in behavior || behavior.tracking === undefined)
                            continue;
                        let trackingEnabled;
                        try {
                            trackingEnabled = yield behavior.tracking.get(cameraConfig.id, [
                                `detect.cameraTracking.${driverKey}`,
                            ]);
                        }
                        catch (error) {
                            if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                                throw error;
                            }
                            // If no response, assume camera doesn't support tracking.
                        }
                        if (trackingEnabled !== undefined) {
                            cameraInfo.tracking = behavior.tracking;
                            break;
                        }
                    }
                }
                // If there is no equipment-specific motion control, then use driver-type-specific
                // motion control.
                if (cameraInfo.motionControl === undefined) {
                    for (const behavior of this.detectedBehaviors) {
                        if (behavior.driverType !== undefined &&
                            !behavior.driverType.includes(this._driverType)) {
                            continue;
                        }
                        if (behavior.motionControl !== undefined) {
                            cameraInfo.motionControl = behavior.motionControl;
                            break;
                        }
                    }
                }
                this.cameraInfo[driverKey] = cameraInfo;
                // Get preset IDs for cameras that require preset commands.
                const usedIds = [];
                Object.values((_d = cameraConfig.presets) !== null && _d !== void 0 ? _d : {}).forEach(preset => {
                    if (preset.pan !== undefined)
                        usedIds.push(preset.pan);
                });
                this.usedIds[driverKey] = usedIds;
            }
            // Some cameras, such as the Atlona AT-HDVS-CAM, go into a deep sleep and don't respond to
            // anything except a power on command. However, they keep their socket open, so we don't
            // enforce idle timeouts if all the cameras have deep sleep.
            this.setIdleTimeoutMs(allCamerasHaveDeepSleep ? 0 : this.powerOnTimeoutMs);
        });
    }
    onConfigure(config) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            // Assign device-specific async handlers.
            this.cameraTracking.asyncHandlers = [];
            for (const cameraInfo of Object.values(this.cameraInfo)) {
                let handlers = (_a = cameraInfo.tracking) === null || _a === void 0 ? void 0 : _a.asyncHandlers;
                if (handlers === undefined)
                    continue;
                if (!Array.isArray(handlers))
                    handlers = [handlers];
                for (const handler of handlers) {
                    if (this.cameraTracking.asyncHandlers.includes(handler))
                        continue;
                    this.cameraTracking.asyncHandlers.push(handler);
                }
            }
            // Run device-specific onConfigure callbacks.
            for (const behavior of this.deviceSpecificBehaviors) {
                if (behavior.driverType !== undefined &&
                    !behavior.driverType.includes(this._driverType)) {
                    continue;
                }
                if (behavior.equipmentTypeIds.includes(config.equipmentType)) {
                    let first = true;
                    for (const cameraConfig of Object.values((_b = config.cameras) !== null && _b !== void 0 ? _b : {})) {
                        if (!('id' in cameraConfig)) {
                            this.log.error(`invalid camera config: ${JSON.stringify(cameraConfig)}`);
                            continue;
                        }
                        yield ((_c = behavior.onConfigure) === null || _c === void 0 ? void 0 : _c.call(behavior, config, cameraConfig, first));
                        first = false;
                    }
                    break;
                }
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.commandResponseStyle = 'standard';
            // Some cameras, like the Atlona AT-HDVS-CAM can go into a deep sleep mode where it doesn't
            // respond to any queries. It will wake up if sent a power on command. So, we send power on
            // commands to all cameras during initialization. The camera may not wake up in time to
            // respond, but it will start waking and it will respond on a future connection attempt.
            for (const cameraConfig of Object.values((_a = config.cameras) !== null && _a !== void 0 ? _a : {})) {
                if (!('id' in cameraConfig)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(cameraConfig)}`);
                    continue;
                }
                let response = undefined;
                // Some cameras (like the PTZ Optics Move SE) restart and drop the network connection
                // when powered on, regardless of whether it was already on. To avoid this loop of
                // initializing, losing connection, and reconnecting, we will query power first and if
                // powered on, we don't need to send a power on command during initialization.
                if ((_b = this.getDeviceSpecificBehavior(config.equipmentType)) === null || _b === void 0 ? void 0 : _b.avoidInitialPowerOn) {
                    try {
                        response = yield this.send([0x80 + cameraConfig.id, 0x09, 0x04, 0x00], (resp) => {
                            if (this.cmdIsCompletion(resp, cameraConfig.id, 3)) {
                                return resp.charCodeAt(2) === 2 ? 'on' : undefined;
                            }
                            return undefined;
                        }, ['initialize.cameras.power'], 
                        // Some PTZ Optics camera can take 6 seconds to respond to query.
                        { sendTimeoutMs: 8000 });
                    }
                    catch (error) {
                        if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                            throw error;
                        }
                    }
                }
                if (response === undefined) {
                    try {
                        // Send a power on command.
                        response = yield this.send([0x80 + cameraConfig.id, 0x01, 0x04, 0x00, 0x02], (resp) => {
                            if (this.cmdIsCompletion(resp, cameraConfig.id))
                                return 'ok';
                            if (this.cmdIsError(resp, cameraConfig.id))
                                return 'noPower';
                            return undefined;
                        }, ['initialize.cameras']);
                    }
                    catch (error) {
                        if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                            throw error;
                        }
                    }
                }
                if (response === undefined) {
                    // Try sending a power query and see if the camera responds. Aver cameras may not
                    // respond to a power command but do respond to a power query.
                    try {
                        response = yield this.send([0x80 + cameraConfig.id, 0x09, 0x04, 0x00], (resp) => {
                            // The Aver CAM550's responses are one byte shorter than standard VISCA
                            // responses. We detect this by looking for a response to a power query,
                            // then we update commandResponseStyle accordingly.
                            if (this.commandResponseStyle === 'standard' &&
                                resp.length === 2 &&
                                resp.charCodeAt(0) === 0x90 &&
                                (resp.charCodeAt(1) === 2 || resp.charCodeAt(1) === 3)) {
                                this.log.info(`AVerCam550 response style detected`);
                                this.commandResponseStyle = 'averCam550';
                            }
                            if (this.cmdIsCompletion(resp, cameraConfig.id, 3))
                                return 'ok';
                            if (this.cmdIsError(resp, cameraConfig.id))
                                return 'noPower';
                            return undefined;
                        }, ['initialize.cameras']);
                    }
                    catch (error) {
                        if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                            throw error;
                        }
                    }
                }
                if (response === undefined) {
                    // Some cameras, such as the Birddog P120 and P200, don't support power and don't
                    // respond at all to power queries, so we look for a response from a zoom query
                    // before deciding the camera doesn't exist.
                    try {
                        response = yield this.send([0x80 + cameraConfig.id, 0x09, 0x04, 0x47], (resp) => {
                            if (this.cmdIsCompletion(resp, cameraConfig.id, 6))
                                return 'ok';
                            if (this.cmdIsError(resp, cameraConfig.id))
                                return 'noZoom';
                            return undefined;
                        }, ['initialize.cameras']);
                    }
                    catch (error) {
                        if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                            throw error;
                        }
                    }
                }
                if (response === undefined) {
                    // Some cameras, as Aver Cam550 have not responded successfully yet, so try a
                    // firmware version query.
                    try {
                        response = yield this.send([0x80 + cameraConfig.id, 0x09, 0x00, 0x02], (resp) => {
                            if (this.cmdIsCompletion(resp, cameraConfig.id, 9))
                                return 'ok';
                            if (this.cmdIsError(resp, cameraConfig.id))
                                return 'noVersion';
                            return undefined;
                        }, ['initialize.cameras', 'version']);
                    }
                    catch (error) {
                        if (!(error instanceof TransportError) || error.code !== 'sendTimeout') {
                            throw error;
                        }
                    }
                }
                if (response === undefined)
                    return 'verifyFailed';
            }
            // Old-school RS-232 VISCA cameras can be daisy-chained. When a camera is added or removed
            // from the chain, we are supposed to get a "network change" message. If this happens, then
            // reconfigure so we can rediscover the cameras.
            this.registerAsync((resp) => {
                return resp.length === 2 &&
                    resp.charCodeAt(0) >= 0x90 &&
                    resp.charCodeAt(0) <= 0xf0 &&
                    resp.charCodeAt(1) === 0x38
                    ? resp
                    : undefined;
            }, (resp) => this.framework.changePhase('configuring'), 'networkChange');
            return 'connected';
        });
    }
    // other class methods
    cmdIsCompletion(cmd, id, length = 2) {
        let retValue = false;
        if (this.commandResponseStyle === 'averCam550' || cmd.length === 1) {
            // Aver CAM550 responses over UDP are missing the second 'socket' byte, so we don't
            // expect it to be there. The Aver Cam550 with firmware 0.0.8004.24 will return just one
            // byte: 0x90 after the 0xFF terminator has been removed. Also, according to Sony VISCA
            // manual, UDP responses will have the value 0x90 as the first byte of the response.
            retValue = cmd.length === length - 1 && cmd.charCodeAt(0) === 0x90;
        }
        else {
            retValue =
                cmd.length === length &&
                    (id === undefined || cmd.charCodeAt(0) === (id + 8) << 4) &&
                    (cmd.charCodeAt(1) & 0x0f0) === 0x50;
        }
        return retValue;
    }
    cmdIsError(cmd, id) {
        return (cmd.length === 3 &&
            (id === undefined || cmd.charCodeAt(0) === (id + 8) << 4) &&
            (cmd.charCodeAt(1) & 0xf0) === 0x60);
    }
    cmdIsNotExecutable(cmd, id) {
        return (cmd.length === 3 &&
            (id === undefined || cmd.charCodeAt(0) === (id + 8) << 4) &&
            (cmd.charCodeAt(1) & 0xf0) === 0x60 &&
            cmd.charCodeAt(2) === 0x41);
    }
    convertPositionToBytes(pos, numBytes) {
        const bytes = [];
        for (let i = 0; i < numBytes; i++) {
            const shift = i * 4;
            bytes.unshift((pos & (0x0f << shift)) >> shift);
        }
        return bytes;
    }
    convertPositionToNumber(data) {
        let value = 0;
        for (let i = 0; i < data.length; i++) {
            value = 16 * value + data.charCodeAt(i);
        }
        return value;
    }
    getDeviceSpecificBehavior(equipmentId) {
        return this.deviceSpecificBehaviors.find(b => b.equipmentTypeIds.includes(equipmentId));
    }
    getPreset(presetId) {
        var _a;
        for (const [configId, cameraInfo] of Object.entries(this.cameraInfo)) {
            const preset = (_a = cameraInfo.presets) === null || _a === void 0 ? void 0 : _a[presetId];
            if (preset !== undefined) {
                return Object.assign(Object.assign({}, preset), { configId, presetId, cameraId: cameraInfo.id });
            }
        }
        return undefined;
    }
    queryPower(tags = ['get.power'], cameraKey) {
        return __awaiter(this, void 0, void 0, function* () {
            if (cameraKey === undefined) {
                // Query the first camera with power support (if there is one).
                for (const key of Object.keys(this.cameraInfo).sort()) {
                    if (this.cameraInfo[key].hasPower) {
                        cameraKey = key;
                        break;
                    }
                }
                if (cameraKey === undefined)
                    return undefined;
            }
            const camera = this.cameraInfo[cameraKey];
            if (camera === undefined) {
                this.log.warning(`no camera with key: ${cameraKey}`);
                return undefined;
            }
            return yield this.send([0x80 + camera.id, 0x09, 0x04, 0x00], (resp) => {
                if (this.cmdIsCompletion(resp, camera.id, 3)) {
                    return resp.charCodeAt(this.commandResponseStyle === 'standard' ? 2 : 1) === 2
                        ? 'on'
                        : 'off';
                }
                // The Unite 150 sends a cancel error in response to the power query when it is
                // powered off.
                if (this.cmdIsNotExecutable(resp, camera.id))
                    return 'off';
                // We could get an error if there are no cameras with power support and we're using
                // the query to keep the connection from timing out.
                if (this.cmdIsError(resp, camera.id))
                    return 'off';
                return undefined;
            }, tags);
        });
    }
    queryZoom(tags = ['get.zoom'], cameraKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const camera = this.cameraInfo[cameraKey];
            if (camera === undefined) {
                this.log.warning(`no camera with key: ${cameraKey}`);
                return undefined;
            }
            yield this.send([0x80 + camera.id, 0x09, 0x04, 0x47], (resp) => {
                if (this.cmdIsCompletion(resp, camera.id, 6))
                    return 'ok';
                if (this.cmdIsError(resp, camera.id))
                    return 'noZoom';
                return undefined;
            }, tags);
        });
    }
    sendCameraMove(action, cameraInfo, tags) {
        var _a, _b, _c, _d, _e, _f, _g;
        return __awaiter(this, void 0, void 0, function* () {
            let cmd = [];
            const panSpeed = cameraInfo.moveSpeed === 'slow'
                ? (_a = cameraInfo.panSpeedSlow) !== null && _a !== void 0 ? _a : 3
                : (_b = cameraInfo.panSpeedFast) !== null && _b !== void 0 ? _b : 8;
            const tiltSpeed = cameraInfo.moveSpeed === 'slow'
                ? (_c = cameraInfo.tiltSpeedSlow) !== null && _c !== void 0 ? _c : 1
                : (_d = cameraInfo.tiltSpeedFast) !== null && _d !== void 0 ? _d : 8;
            const zoomSpeed = cameraInfo.moveSpeed === 'slow'
                ? (_e = cameraInfo.zoomSpeedSlow) !== null && _e !== void 0 ? _e : 1
                : (_f = cameraInfo.zoomSpeedFast) !== null && _f !== void 0 ? _f : 3;
            const direction = cameraInfo.moveDirection === 'left'
                ? cameraInfo.invertPan
                    ? 'right'
                    : 'left'
                : cameraInfo.moveDirection === 'right'
                    ? cameraInfo.invertPan
                        ? 'left'
                        : 'right'
                    : cameraInfo.moveDirection;
            if (action === 'start') {
                if (direction === 'left') {
                    cmd = [0x80 + cameraInfo.id, 0x01, 0x06, 0x01, panSpeed, tiltSpeed, 0x01, 0x03];
                }
                else if (direction === 'right') {
                    cmd = [0x80 + cameraInfo.id, 0x01, 0x06, 0x01, panSpeed, tiltSpeed, 0x02, 0x03];
                }
                else if (direction === 'down') {
                    cmd = [0x80 + cameraInfo.id, 0x01, 0x06, 0x01, panSpeed, tiltSpeed, 0x03, 0x02];
                }
                else if (direction === 'up') {
                    cmd = [0x80 + cameraInfo.id, 0x01, 0x06, 0x01, panSpeed, tiltSpeed, 0x03, 0x01];
                }
                else if (direction === 'out') {
                    cmd = [0x80 + cameraInfo.id, 0x01, 0x04, 0x07, 0x30 + zoomSpeed];
                }
                else if (direction === 'in') {
                    cmd = [0x80 + cameraInfo.id, 0x01, 0x04, 0x07, 0x20 + zoomSpeed];
                }
                else {
                    this.log.error(`invalid move direction: ${cameraInfo.moveDirection}`);
                    return;
                }
            }
            else if (action === 'stop') {
                if (['left', 'right', 'up', 'down'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = [0x80 + cameraInfo.id, 0x01, 0x06, 0x01, panSpeed, tiltSpeed, 0x03, 0x03];
                }
                else if (['in', 'out'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = [0x80 + cameraInfo.id, 0x01, 0x04, 0x07, 0x00];
                }
                else {
                    this.log.error(`invalid move direction: ${cameraInfo.moveDirection}`);
                    return;
                }
            }
            else if (action === 'continue') {
                // nop
            }
            if (cameraInfo.moveTimeout) {
                cameraInfo.moveTimeout.clear();
                cameraInfo.moveTimeout = undefined;
            }
            if (cmd.length > 0) {
                yield ((_g = cameraInfo === null || cameraInfo === void 0 ? void 0 : cameraInfo.motionControl) === null || _g === void 0 ? void 0 : _g.set(cameraInfo.id, cmd, tags));
            }
            if (action === 'start' || action === 'continue') {
                cameraInfo.moveTimeout = this.timeouts.set(() => __awaiter(this, void 0, void 0, function* () {
                    cameraInfo.moveTimeout = undefined;
                    this.log.warning(`camera move timeout for camera id ${cameraInfo.id}`);
                    yield this.sendCameraMove('stop', cameraInfo, tags);
                }), 1000);
            }
        });
    }
}
